import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "react-query";
import Papa from "papaparse";
import { QK } from "../../../base/qk";
import { searchClients } from "../../../api/services/Client";
import { getMedicineReports } from "../../../api/services/reports";
import CustomDate from "../../CustomForm/CustomDate";
import SearchableSelect from "../../FormFields/CustomSelect/SearchableSelect";

const MedicineReport = () => {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [serviceUserOption, setServiceUserOption] = useState("all");
  const [client, setClient] = useState("");
  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [reportData, setReportData] = useState([]);

  const { data: clients, isLoading: clientsLoading } = useQuery(
    [QK.searchClients, search],
    () => searchClients(search),
    { enabled: search.length >= 2 }
  );

  const handleSubmit = async () => {
    try {
      const data = await getMedicineReports(dateFrom, dateTo, client);
      setReportData(data);
    } catch (error) {
      console.error("Error fetching medicine reports:", error);
    }
  };

  const clientOptions = clients
    ? clients.map((client) => ({
        value: client.id,
        label: `${client.first_name} ${client.last_name}`,
      }))
    : [];

  const handleInputChange = (inputValue) => {
    setInputValue(inputValue);
    if (inputValue.length >= 2) {
      setSearch(inputValue);
    }
  };

  const handleDownloadCSV = () => {
    if (reportData.length === 0) return;
    const csv = Papa.unparse(reportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "medicine_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper elevation={1} sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Medicine Report
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <CustomDate
              label="Date From"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <CustomDate
              label="Date To"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              required
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={{ xs: 12, md: 8 }}>
            <RadioGroup
              row
              value={serviceUserOption}
              onChange={(e) => setServiceUserOption(e.target.value)}
            >
              <FormControlLabel
                value="all"
                control={<Radio />}
                label="All Service Users"
              />
              <FormControlLabel
                value="select"
                control={<Radio />}
                label="Select Service User"
              />
            </RadioGroup>

            {serviceUserOption === "select" && (
              <Box sx={{ mt: 1 }}>
                <SearchableSelect
                  options={clientOptions}
                  isLoading={clientsLoading}
                  onChange={setClient}
                  value={client}
                  inputValue={inputValue}
                  onInputChange={handleInputChange}
                  placeholder="Search Clients, write at least 2 characters"
                />
              </Box>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={!dateFrom || !dateTo}
              >
                Generate Report
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {reportData.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Report Data
            </Typography>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDownloadCSV}
            >
              Download CSV
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Client Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((report) => (
                  <TableRow key={report.id} hover>
                    <TableCell>{report.id}</TableCell>
                    <TableCell>{report.client_name}</TableCell>
                    <TableCell>{report.date}</TableCell>
                    <TableCell>{report.time}</TableCell>
                    <TableCell>{report.status}</TableCell>
                    <TableCell>{report.reason || "N/A"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Container>
  );
};

export default MedicineReport;